'use client';

import { useState } from 'react';

import { motion, AnimatePresence } from 'framer-motion';
import {
  ArrowRight,
  ArrowLeft,
  Check,
  Mail,
  BookOpen,
  Sparkles,
} from 'lucide-react';

import { useTranslation } from '@/lib/i18n/context';

export function AIAdoptionGuide() {
  const { t, isRTL } = useTranslation();
  const [isUnlocked, setIsUnlocked] = useState(false);
  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [currentChapter, setCurrentChapter] = useState(1);
  const [completedChapters, setCompletedChapters] = useState<number[]>([]);

  // Guide chapters
  const CHAPTERS = [
    {
      id: 1,
      title: t.tools_pages['The Current State of Things'],
      readTime: `3 ${t.testimonials['min read'] || 'min'}`,
    },
    {
      id: 2,
      title: t.tools_pages['Where to Start'],
      readTime: `4 ${t.testimonials['min read'] || 'min'}`,
    },
    {
      id: 3,
      title: t.tools_pages['How to Actually Do This'],
      readTime: `5 ${t.testimonials['min read'] || 'min'}`,
    },
    {
      id: 4,
      title: t.tools_pages['What Goes Wrong'],
      readTime: `4 ${t.testimonials['min read'] || 'min'}`,
    },
    {
      id: 5,
      title: t.tools_pages['What to Expect'],
      readTime: `3 ${t.testimonials['min read'] || 'min'}`,
    },
    {
      id: 6,
      title: t.tools_pages['Where to Go From Here'],
      readTime: `3 ${t.testimonials['min read'] || 'min'}`,
    },
  ];

  const handleUnlock = (e: React.FormEvent) => {
    e.preventDefault();
    if (!email || !email.includes('@') || submitting) return;
    setSubmitting(true);
    // Lead capture is best-effort and must never block the user. Fire it in the
    // background (keepalive lets it finish even if the page unloads) and unlock
    // immediately, no matter how slow or unresponsive the upstream is.
    void fetch('/api/tools/email-capture', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, tool: 'AI Adoption Guide' }),
      keepalive: true,
    }).catch(() => {});
    setIsUnlocked(true);
  };

  const markChapterComplete = (chapterId: number) => {
    if (!completedChapters.includes(chapterId)) {
      setCompletedChapters([...completedChapters, chapterId]);
    }
  };

  const goToNextChapter = () => {
    markChapterComplete(currentChapter);
    if (currentChapter < CHAPTERS.length) {
      setCurrentChapter(currentChapter + 1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const goToPrevChapter = () => {
    if (currentChapter > 1) {
      setCurrentChapter(currentChapter - 1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const progress = (completedChapters.length / CHAPTERS.length) * 100;

  // Email gate screen
  if (!isUnlocked) {
    return (
      <section className="min-h-screen bg-white pt-32 pb-20 md:pt-40">
        <div className="container max-w-5xl">
          <div className="grid gap-12 lg:grid-cols-2 lg:gap-16">
            {/* Left: Content preview */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5 }}
            >
              <div className="mb-6 inline-flex items-center gap-2 rounded-full bg-blue-100 px-3 py-1 text-sm font-medium text-blue-700">
                <BookOpen className="size-4" />
                {t.tools_pages['Free Guide • 28 min read']}
              </div>

              <h1 className="mb-4 text-3xl md:text-4xl lg:text-5xl">
                {t.tools_pages['A Practical Guide to AI in Customer Support']}
              </h1>

              <p className="mb-8 text-lg text-neutral-600">
                {t.tools_pages["What works, what doesn't, and how to think about adopting AI for your support team."]}
              </p>

              {/* Key insight teaser */}
              <div className="mb-8 rounded-2xl bg-neutral-50 p-6">
                <p className="text-lg text-neutral-700">
                  {t.tools_pages["A practical guide based on what we've learned helping teams implement AI support—covering the approach, timeline, and common pitfalls."]}
                </p>
              </div>

              {/* Chapter preview */}
              <div className="space-y-3">
                <div className="text-sm font-medium text-neutral-500">
                  {t.tools_pages['6 Chapters Inside:']}
                </div>
                {CHAPTERS.map((chapter, index) => (
                  <div
                    key={chapter.id}
                    className="flex items-center gap-3 text-sm"
                  >
                    <div className="flex size-6 items-center justify-center rounded-full bg-neutral-100 text-xs font-medium text-neutral-500">
                      {index + 1}
                    </div>
                    <span className="text-neutral-700">{chapter.title}</span>
                    <span className="text-neutral-400">• {chapter.readTime}</span>
                  </div>
                ))}
              </div>
            </motion.div>

            {/* Right: Email form */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
              className="lg:sticky lg:top-32 lg:self-start"
            >
              <div className="rounded-2xl border border-neutral-200 bg-white p-8 shadow-xl">
                <div className="mb-6 text-center">
                  <div className="mb-4 inline-flex size-14 items-center justify-center rounded-full bg-black">
                    <Sparkles className="size-6 text-white" />
                  </div>
                  <h2 className="text-xl">{t.tools_pages['Get the Full Guide']}</h2>
                  <p className="mt-1 text-sm text-neutral-500">
                    {t.tools_pages['Instant access to all 6 chapters']}
                  </p>
                </div>

                <form onSubmit={handleUnlock} className="space-y-4">
                  <div className="relative">
                    <Mail className={`absolute top-1/2 ${isRTL ? 'right-4' : 'left-4'} size-5 -translate-y-1/2 text-neutral-400`} />
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder={t.tools_pages['Enter your work email']}
                      className={`w-full rounded-xl border border-neutral-300 py-4 pr-4 ${isRTL ? 'pl-4 pr-12' : 'pl-12'} text-base transition-all outline-none focus:border-black focus:ring-2 focus:ring-black/10`}
                      required
                    />
                  </div>
                  <button
                    type="submit"
                    disabled={submitting || !email.includes('@')}
                    className="flex w-full items-center justify-center gap-2 rounded-xl bg-black py-4 font-semibold text-white transition-all hover:bg-neutral-800 disabled:cursor-not-allowed disabled:bg-neutral-200 disabled:text-neutral-400"
                  >
                    {t.tools_pages['Read the Guide']}
                    <ArrowRight className={`size-5 ${isRTL ? 'rotate-180' : ''}`} />
                  </button>
                </form>

                <p className="mt-4 text-center text-xs text-neutral-400">
                  {t.tools_pages['No spam, ever. Unsubscribe anytime.']}
                </p>

                {/* Trust signals */}
                <div className="mt-6 border-t border-neutral-100 pt-6">
                  <div className="grid grid-cols-3 gap-4 text-center">
                    <div>
                      <div className="text-lg font-bold text-black">15+</div>
                      <div className="text-xs text-neutral-500">{t.tools_pages['Sources cited']}</div>
                    </div>
                    <div>
                      <div className="text-lg font-bold text-black">
                        {new Date().getFullYear()}
                      </div>
                      <div className="text-xs text-neutral-500">{t.tools_pages['Latest data']}</div>
                    </div>
                    <div>
                      <div className="text-lg font-bold text-black">350%</div>
                      <div className="text-xs text-neutral-500">{t.tools_pages['Avg ROI']}</div>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>
    );
  }

  // Main guide content
  return (
    <section className="min-h-screen bg-white pt-32 pb-20 md:pt-40">
      <div className="container max-w-2xl">
        {/* Progress bar */}
        <div className="fixed top-0 right-0 left-0 z-50 bg-white/80 backdrop-blur-sm">
          <div className="h-0.5 bg-neutral-100">
            <motion.div
              className="h-full bg-black"
              initial={{ width: 0 }}
              animate={{ width: `${progress}%` }}
              transition={{ duration: 0.3 }}
            />
          </div>
        </div>

        {/* Chapter navigation - minimal */}
        <div className="mb-12 flex items-center justify-between border-b border-neutral-100 pb-4">
          <div className="flex items-center gap-3">
            {CHAPTERS.map((chapter) => (
              <button
                key={chapter.id}
                onClick={() => setCurrentChapter(chapter.id)}
                className={`flex size-8 items-center justify-center rounded-full text-sm font-medium transition-all ${currentChapter === chapter.id
                    ? 'bg-black text-white'
                    : completedChapters.includes(chapter.id)
                      ? 'bg-neutral-200 text-neutral-600'
                      : 'text-neutral-400 hover:text-neutral-600'
                  }`}
              >
                {completedChapters.includes(chapter.id) ? (
                  <Check className="size-4" />
                ) : (
                  chapter.id
                )}
              </button>
            ))}
          </div>
          <span className="text-sm text-neutral-400">
            {CHAPTERS[currentChapter - 1].readTime}
          </span>
        </div>

        <AnimatePresence mode="wait">
          {/* Chapter 1: Why AI, Why Now */}
          {currentChapter === 1 && (
            <motion.div
              key="chapter1"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className="prose prose-lg prose-gray max-w-none"
            >
              <p className="text-sm font-medium tracking-wide text-neutral-400 uppercase">
                {t.tools_pages['Chapter 1']}
              </p>
              <h1 className="mt-2 mb-6 text-4xl">
                {t.tools_pages['The Current State of Things']}
              </h1>

              <p className="lead text-xl text-neutral-600">
                {t.tools_pages["AI in customer support has reached a turning point. Here's what that means for your team."]}
              </p>

              <p>
                {t.tools_pages['AI in customer support has gotten genuinely good. The technology has matured to the point where it can handle a meaningful portion of support requests—not just simple FAQs, but nuanced questions that require understanding context and taking actions.']}
              </p>

              <p>
                {t.tools_pages['Roughly 80% of companies have deployed some form of AI support, and 89% plan to by 2026. The market is projected to reach $74 billion by 2032. These aren\'t speculative numbers—this is happening now.']}
              </p>

              <h2 className="mt-12 text-2xl">{t.tools_pages['What good looks like']}</h2>

              <p>
                {t.tools_pages['The companies getting meaningful results from AI support share some common traits. They have decent documentation. They start with a focused scope. They keep humans involved for complex issues. And they think of AI as a tool to help their team, not replace it.']}
              </p>

              <p>
                {t.tools_pages['When it works well, the benefits are significant: faster response times, happier customers, and support teams that can focus on genuinely interesting problems instead of answering the same questions repeatedly.']}
              </p>

              <h2 className="mt-12 text-2xl">{t.tools_pages['The approach matters']}</h2>

              <p>
                {t.tools_pages["The technology works. But about 67% of implementations don't deliver the expected ROI in the first year. The difference between success and disappointment usually isn't the AI itself—it's how it's implemented."]}
              </p>

              <p>
                {t.tools_pages["That's what this guide covers: the approach that works, the timeline to expect, and the mistakes to avoid."]}
              </p>

              <div className="my-10 rounded-xl bg-neutral-50 p-6">
                <p className="m-0 text-neutral-700">
                  {t.tools_pages["This guide will help you understand what's involved, plan an approach that fits your situation, and set realistic expectations for the journey."]}
                </p>
              </div>
            </motion.div>
          )}

          {/* Chapter 2: Integration-First Approach */}
          {currentChapter === 2 && (
            <motion.div
              key="chapter2"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className="prose prose-lg prose-gray max-w-none"
            >
              <p className="text-sm font-medium tracking-wide text-neutral-400 uppercase">
                {t.tools_pages['Chapter 2']}
              </p>
              <h1 className="mt-2 mb-6 text-4xl">
                {t.tools_pages['Where to Start']}
              </h1>

              <p className="lead text-xl text-neutral-600">
                {t.tools_pages["In an ideal world, you'd build your support system from scratch with AI at the core. But that's rarely realistic."]}
              </p>

              <p>
                {t.tools_pages["Here's the situation most companies are in: you probably have an existing helpdesk—Zendesk, Intercom, Freshdesk, or something similar. Your team knows how to use it. Your workflows are built around it. You have years of customer data and conversation history there."]}
              </p>

              <p>
                {t.tools_pages['An AI-native solution would theoretically be better. When AI is at the center of the system rather than bolted on, it can do more. It understands context better. It can take actions more naturally. The experience is smoother for everyone.']}
              </p>

              <p>
                {t.tools_pages['But switching to a completely new system is a massive undertaking. Most companies that try to do it all at once struggle. The migration takes longer than expected. Things break. The team is frustrated. Customers notice the disruption.']}
              </p>

              <h2 className="mt-12 text-2xl">
                {t.tools_pages['A more realistic path']}
              </h2>

              <p>
                {t.tools_pages['What actually works for most companies is to start alongside your existing system. Find an AI tool that can work with your current helpdesk—connecting through APIs to handle conversations while keeping everything in one place.']}
              </p>

              <p>
                {t.tools_pages["This isn't the perfect end state. But it lets you learn what AI can do for your specific situation without betting everything on a big migration. You can see what works and what doesn't. Your team can adapt gradually."]}
              </p>

              <p>
                {t.tools_pages["Then, once you understand what you actually need—not what you thought you'd need—you can make a more informed decision about whether to go deeper. Maybe you eventually move to a fully AI-native system. Maybe the hybrid approach turns out to be enough. Either way, you'll know based on real experience rather than speculation."]}
              </p>

              <h2 className="mt-12 text-2xl">{t.tools_pages['What to look for']}</h2>

              <p>
                {t.tools_pages["If you're evaluating AI tools, look for ones that offer this kind of gradual path. Can it work alongside your existing system? Can you start with just a portion of your tickets and expand from there? Is there a path to deeper integration if you want it later?"]}
              </p>

              <p>
                {t.tools_pages["Be skeptical of vendors who insist you need to replace everything immediately. That approach might make sense eventually, but it's usually not the right first step."]}
              </p>

              <div className="my-10 rounded-xl bg-neutral-50 p-6">
                <p className="m-0 text-neutral-700">
                  {t.tools_pages["The goal isn't to find the theoretically perfect solution—it's to find a realistic path forward that you can actually execute without breaking things."]}
                </p>
              </div>
            </motion.div>
          )}

          {/* Chapter 3: Implementation Roadmap */}
          {currentChapter === 3 && (
            <motion.div
              key="chapter3"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className="prose prose-lg prose-gray max-w-none"
            >
              <p className="text-sm font-medium tracking-wide text-neutral-400 uppercase">
                {t.tools_pages['Chapter 3']}
              </p>
              <h1 className="mt-2 mb-6 text-4xl">
                {t.tools_pages['How to Actually Do This']}
              </h1>

              <p className="lead text-xl text-neutral-600">
                {t.tools_pages['Your timeline depends on one thing: how ready you are.']}
              </p>

              <p>
                {t.tools_pages["Here's something that might surprise you: if your company is AI-ready, you can genuinely go live in days or weeks. Not a watered-down version—actually handling real customer conversations and resolving tickets."]}
              </p>

              <p>
                {t.tools_pages['The question isn\'t "how long does AI implementation take?" It\'s "how ready is your company?"']}
              </p>

              <h2 className="mt-12 text-2xl">{t.tools_pages['What AI-ready means']}</h2>

              <p>
                {t.tools_pages['Companies that can move fast typically have a few things in place:']}
              </p>

              <p>
                <strong>{t.tools_pages['A mainstream support stack.']}</strong> {t.tools_pages["Zendesk, Intercom, Freshdesk, HubSpot—the standard platforms. Modern AI tools integrate with these out of the box. If you're on something custom or unusual, that adds time."]}
              </p>

              <p>
                <strong>{t.tools_pages['Decent documentation.']}</strong> {t.tools_pages["You don't need perfect docs, but you need something. Help articles, FAQs, product guides. AI learns from this. If your knowledge base is empty or badly outdated, that needs to be fixed first."]}
              </p>

              <p>
                <strong>{t.tools_pages['Clear actions.']}</strong> {t.tools_pages['What should the AI actually be able to do? Answer questions? Reset passwords? Check order status? Process refunds? The clearer you are on this, the faster you can move.']}
              </p>

              <h2 className="mt-12 text-2xl">{t.tools_pages["If you're ready"]}</h2>

              <p>
                {t.tools_pages["If you have these basics in place, implementation can be fast. Connect your helpdesk, point AI at your docs, define what it should handle, and you're live. You can start with a portion of traffic to build confidence, then expand."]}
              </p>

              <h2 className="mt-12 text-2xl">
                {t.tools_pages['If you need prep work']}
              </h2>

              <p>
                {t.tools_pages["If your docs are sparse or outdated, spend time on that first. AI is only as good as what it knows. This isn't a blocker—it's just work that needs to happen. Some companies do this in a week, some take a month. Depends on your starting point."]}
              </p>

              <h2 className="mt-12 text-2xl">{t.tools_pages["After you're live"]}</h2>

              <p>
                {t.tools_pages["Going live isn't the end—it's the beginning. You'll learn what works and what needs adjustment. Customer questions evolve. Your product changes. Good implementations include ongoing attention: reviewing what the AI handles, updating knowledge, expanding scope."]}
              </p>

              <div className="my-10 rounded-xl bg-neutral-50 p-6">
                <p className="m-0 text-neutral-700">
                  {t.tools_pages["The timeline isn't fixed. Ready companies go live in weeks. Others need prep time. Either way, the goal is getting to value—not following an arbitrary schedule."]}
                </p>
              </div>
            </motion.div>
          )}

          {/* Chapter 4: Common Mistakes */}
          {currentChapter === 4 && (
            <motion.div
              key="chapter4"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className="prose prose-lg prose-gray max-w-none"
            >
              <p className="text-sm font-medium tracking-wide text-neutral-400 uppercase">
                {t.tools_pages['Chapter 4']}
              </p>
              <h1 className="mt-2 mb-6 text-4xl">
                {t.tools_pages['What Goes Wrong']}
              </h1>

              <p className="lead text-xl text-neutral-600">
                {t.tools_pages["Learning from the 67% of implementations that don't work out."]}
              </p>

              <p>
                {t.tools_pages["When AI projects fail, it's rarely because the AI technology itself was bad. Usually it's the implementation—the decisions around how to roll it out and what to expect."]}
              </p>

              <h2 className="mt-10 text-xl">
                {t.tools_pages['Launching before being ready']}
              </h2>

              <p>
                {t.tools_pages["The most common issue: launching AI without the foundations in place. No documentation for the AI to learn from. No clear definition of what it should handle. No plan for what happens when it can't answer something."]}
              </p>

              <p>
                {t.tools_pages["Speed isn't the problem—lack of readiness is. Companies that are ready can go live quickly and see great results. Companies that skip the basics struggle regardless of timeline."]}
              </p>

              <h2 className="mt-10 text-xl">{t.tools_pages['Bad documentation']}</h2>

              <p>
                {t.tools_pages["AI learns from your knowledge base. If your documentation is outdated, incomplete, or poorly organized, the AI will give bad answers. It's not smart enough to know that the information it learned is wrong."]}
              </p>

              <p>
                {t.tools_pages['This is fixable, but you have to actually do the work. Most companies underestimate how much effort it takes to get documentation AI-ready.']}
              </p>

              <h2 className="mt-10 text-xl">{t.tools_pages['Ignoring the team']}</h2>

              <p>
                {t.tools_pages["Support agents often see AI as a threat to their jobs. If you don't involve them in the process, they'll be skeptical at best and actively hostile at worst. They'll find reasons why the AI doesn't work, because they're looking for them."]}
              </p>

              <p>
                {t.tools_pages["The good implementations treat AI as something that helps agents do their jobs better, not something that replaces them."]}
              </p>

              <h2 className="mt-10 text-xl">{t.tools_pages['Automating too much']}</h2>

              <p>
                {t.tools_pages["There's a temptation to automate everything. But some situations—frustrated customers, complex problems, sensitive issues—actually need a human. Companies that try to handle these with AI usually make things worse."]}
              </p>

              <h2 className="mt-10 text-xl">{t.tools_pages['Set it and forget it']}</h2>

              <p>
                {t.tools_pages['AI isn\'t a one-time project. It needs ongoing attention. Customer questions change. Products change. New edge cases come up. Companies that treat AI as "done" after the initial rollout are often surprised when it stops working well six months later.']}
              </p>

              <div className="my-10 rounded-xl bg-neutral-50 p-6">
                <p className="m-0 text-neutral-700">
                  {t.tools_pages["Most of these mistakes come from treating AI implementation like buying software. It's not. It's more like hiring a new team member who needs training and oversight."]}
                </p>
              </div>
            </motion.div>
          )}

          {/* Chapter 5: Timeline & ROI */}
          {currentChapter === 5 && (
            <motion.div
              key="chapter5"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className="prose prose-lg prose-gray max-w-none"
            >
              <p className="text-sm font-medium tracking-wide text-neutral-400 uppercase">
                {t.tools_pages['Chapter 5']}
              </p>
              <h1 className="mt-2 mb-6 text-4xl">
                {t.tools_pages['What to Expect']}
              </h1>

              <p className="lead text-xl text-neutral-600">
                {t.tools_pages['Honest expectations about timeline and results.']}
              </p>

              <p>
                {t.tools_pages["You'll see various ROI claims in the AI industry—350%, 400%, sometimes higher. These numbers are real, but they're also cherry-picked from the best cases. Not everyone gets these results."]}
              </p>

              <p>
                {t.tools_pages['What you can realistically expect depends a lot on your specific situation: how good your documentation is, how complex your product is, what kinds of questions customers ask, how well you execute the implementation.']}
              </p>

              <h2 className="mt-12 text-2xl">{t.tools_pages['The first few months']}</h2>

              <p>
                {t.tools_pages["Early on, you're mostly learning. The AI will handle some simple stuff—password resets, basic questions, order status. Your team will spend time monitoring what works and fixing what doesn't. You might see some efficiency gains, but this period is more about building a foundation."]}
              </p>

              <h2 className="mt-12 text-2xl">{t.tools_pages['Getting to useful']}</h2>

              <p>
                {t.tools_pages['Around 3-6 months in, if things are going well, the AI starts becoming genuinely useful. It handles a meaningful chunk of tickets on its own. Your team has more time for complex problems. Response times improve. Customer satisfaction often goes up because people get faster answers.']}
              </p>

              <h2 className="mt-12 text-2xl">{t.tools_pages['The longer term']}</h2>

              <p>
                {t.tools_pages["Companies that do this well often end up automating 50-70% of routine inquiries. The economics can be compelling—it costs much less for AI to answer a simple question than for a person to. But the bigger benefit is often qualitative: your support team can focus on work that's actually interesting, customers get help faster, and you can offer support that would have been too expensive otherwise (like 24/7 coverage)."]}
              </p>

              <div className="my-10 rounded-xl bg-neutral-50 p-6">
                <p className="m-0 text-neutral-700">
                  {t.tools_pages['Results vary depending on your situation. Some companies see dramatic improvements. Others find it helpful but not transformative. The best approach is to start small, see what you learn, and make decisions based on actual results rather than projections.']}
                </p>
              </div>
            </motion.div>
          )}

          {/* Chapter 6: Getting Started */}
          {currentChapter === 6 && (
            <motion.div
              key="chapter6"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className="prose prose-lg prose-gray max-w-none"
            >
              <p className="text-sm font-medium tracking-wide text-neutral-400 uppercase">
                {t.tools_pages['Chapter 6']}
              </p>
              <h1 className="mt-2 mb-6 text-4xl">
                {t.tools_pages['Where to Go From Here']}
              </h1>

              <p className="lead text-xl text-neutral-600">
                {t.tools_pages['Some practical next steps, depending on where you are.']}
              </p>

              <h2 className="mt-12 text-2xl">
                {t.tools_pages["If you're still deciding"]}
              </h2>

              <p>
                {t.tools_pages['Start by understanding your current situation better. What are the most common questions your support team answers? How good is your documentation? How much time does your team spend on repetitive vs. complex issues?']}
              </p>

              <p>
                {t.tools_pages['We have a readiness assessment that can help you think through this, and a calculator to help estimate what the impact might look like. These aren\'t meant to convince you—they\'re meant to help you think through whether this makes sense for your specific situation.']}
              </p>

              <h2 className="mt-12 text-2xl">
                {t.tools_pages['If you want to learn more']}
              </h2>

              <p>
                {t.tools_pages["Talk to companies that have done this. Ask them what worked and what didn't. Case studies from vendors only show the successes, so try to find people who will share the full picture."]}
              </p>

              <p>
                {t.tools_pages["If you want to talk to us, we're happy to answer questions even if you're not sure Open is right for you. We'd rather help you make the right decision than push you into something that doesn't fit."]}
              </p>

              <h2 className="mt-12 text-2xl">
                {t.tools_pages["If you're ready to try"]}
              </h2>

              <p>
                {t.tools_pages['Look for a solution that lets you start small. Can you run it alongside your existing system? Can you start with just one channel or one type of question? Is there a way to test without making a big commitment?']}
              </p>

              <p>
                {t.tools_pages['We built Open with this in mind—it works alongside Zendesk, Intercom, and other helpdesks, and you can start with a small pilot before expanding. But there are other options too. The right choice depends on your specific situation.']}
              </p>

              <div className="not-prose my-12 rounded-xl bg-neutral-50 p-8">
                <p className="text-neutral-700">
                  {t.tools_pages['If you want to explore whether Open might be a good fit, you\'re welcome to schedule a conversation. We\'ll try to be helpful regardless of whether you end up using our product.']}
                </p>
              </div>

              <p className="text-neutral-500">
                {t.tools_pages['Thanks for reading. We hope this was useful.']}
              </p>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Chapter navigation */}
        <div className="mt-16 flex items-center justify-between border-t border-neutral-100 pt-8">
          <button
            onClick={goToPrevChapter}
            disabled={currentChapter === 1}
            className={`flex items-center gap-2 text-sm font-medium transition-all ${currentChapter === 1
                ? 'cursor-not-allowed text-neutral-300'
                : 'text-neutral-600 hover:text-black'
              }`}
          >
            <ArrowLeft className="size-4" />
            {t.tools_pages['Previous']}
          </button>

          <button
            onClick={goToNextChapter}
            disabled={currentChapter === CHAPTERS.length}
            className={`flex items-center gap-2 rounded-full px-6 py-2 text-sm font-semibold transition-all ${currentChapter === CHAPTERS.length
                ? 'cursor-not-allowed bg-neutral-100 text-neutral-400'
                : 'bg-black text-white hover:bg-neutral-800'
              }`}
          >
            {currentChapter === CHAPTERS.length
              ? t.tools_pages['Guide Complete']
              : t.tools_pages['Next Chapter']}
            {currentChapter !== CHAPTERS.length && (
              <ArrowRight className={`size-4 ${isRTL ? 'rotate-180' : ''}`} />
            )}
          </button>
        </div>
      </div>
    </section>
  );
}
